import Service, { inject as service } from '@ember/service';
import {
  updateLogger,
  sentryUpdateLogger,
} from '@banking/ui/lib/workflow/update-logger';
import { tracked } from '@glimmer/tracking';
import workflow, {
  Action,
  State,
  createState,
  browserHistoryChangeAction,
} from '@banking/ui/workflows/onboarding-kyb';
import {
  BusinessManagementScreenData,
  EntityTypeScreenData,
  MAX_OWNERSHIP_BPS,
} from '@banking/ui/workflows/onboarding-kyb/state';
import { KybDataSave } from '@banking/ui/workflows/onboarding-kyb/update';
import RouterService from '@ember/routing/router-service';
import FeaturesService from '@square/ember-features/services/features';
import { ApplicationFlowUpdate } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow_update';
import { LegalEntitiesBusinessEntityType } from '@banking/api/utils/business-types';
import CurrentApplicationFlowService from '@banking/api/services/current-application-flow';
import { distinctUntilChanged, map, Subscription } from 'rxjs';
import ErrorHandler from '@banking/savings-onboarding-app/services/error-handler';
import { ConnectError } from '@square/ember-rpc-client/utils';
import { generateIndividualClientId } from '@banking/ui/workflows/onboarding-ci/update';
import SFSIndividualApi from '@banking/savings-onboarding-app/utils/sfs-individual-api';

export default class KybWorkflow extends Service {
  @service router!: RouterService;
  @service features!: FeaturesService;
  @service errorHandler!: ErrorHandler;
  @service currentApplicationFlow!: CurrentApplicationFlowService;

  #stateSubscription!: Subscription;
  #routeSubscription!: Subscription;
  #loggerSubscription!: Subscription;
  #sentryLoggerSubscription!: Subscription;
  #popstateHandler?: () => void;

  emit!: (action: Action) => void;
  @tracked state: State = createState();

  routeForScreen(screen: State.Screen): string {
    switch (screen) {
      case State.Screen.BusinessType:
        return 'signup.business-type';
      case State.Screen.EntityType:
        return 'signup.entity-type';
      case State.Screen.BusinessManagement:
        return 'signup.business-management';
      case State.Screen.ControllingIndividuals:
        return 'signup.controlling-individuals';
      case State.Screen.BusinessAddress:
      case State.Screen.None:
        throw new Error('no route for none screen');
    }
  }

  // eslint-disable-next-line ember/classic-decorator-hooks
  init() {
    super.init();
    this.setupWorkflow();
  }

  setupWorkflow() {
    if (!this.currentApplicationFlow.id) {
      throw new Error('cannot make workflow with no app flow id');
    }

    const context = {
      kybEnabled: true,
      individualIdGenerator: generateIndividualClientId,
      individualApi: new SFSIndividualApi(
        this.currentApplicationFlow.id,
        this.currentApplicationFlow,
        (error: unknown) => {
          this.errorHandler.handleRpcError(error as ConnectError);
        }
      ),
      saveKybData: (data: KybDataSave): Promise<void> => {
        return this.saveAfterScreenAdvance(data);
      },
    };

    const { emit, states, updates } = workflow(createState(), context);
    this.emit = emit;
    this.#stateSubscription = states.subscribe((state) => {
      this.state = state;
    });
    this.#routeSubscription = states
      .pipe(
        map(({ screen, done }) => ({ screen, done })),
        distinctUntilChanged(
          (previous, current) =>
            previous.screen === current.screen && previous.done === current.done
        )
      )
      .subscribe(({ screen, done }) => this.setRoute(screen, done));

    this.#loggerSubscription = updates.subscribe(
      updateLogger('Onboarding KYB')
    );
    this.#sentryLoggerSubscription = updates.subscribe(
      sentryUpdateLogger('Onboarding KYB')
    );
    this.#popstateHandler = () => {
      this.emit(browserHistoryChangeAction(this.router.currentRouteName));
    };
    window.addEventListener('popstate', this.#popstateHandler);
  }

  willDestroy() {
    this.#stateSubscription.unsubscribe();
    this.#routeSubscription.unsubscribe();
    this.#loggerSubscription.unsubscribe();
    this.#sentryLoggerSubscription.unsubscribe();
    if (this.#popstateHandler) {
      window.removeEventListener('popstate', this.#popstateHandler);
    }
  }

  setRoute(screen: State.Screen, done: boolean): void {
    if (screen !== State.Screen.None) {
      const route = this.routeForScreen(screen);
      this.router.transitionTo(route);
    } else {
      if (done) {
        this.router.transitionTo('signup.agreement');
      } else {
        if (
          this.features.isEnabled('banking/stop_prefilling_savings_onboarding')
        ) {
          this.router.transitionTo('signup.enter-business-info');
        } else {
          this.router.transitionTo('signup.confirm-business.index');
        }
      }
    }
  }

  saveAfterScreenAdvance(kybData: KybDataSave): Promise<void> {
    switch (kybData.screen) {
      case State.Screen.BusinessType:
        if (kybData.data.businessType === 'individual') {
          return this.saveSolePropEntityType();
        }
        break;
      case State.Screen.EntityType:
        return this.saveEntityTypePage(kybData.data);
      case State.Screen.BusinessManagement:
        return this.saveBusinessManagmentPage(kybData.data);
    }

    return Promise.resolve();
  }

  async saveSolePropEntityType() {
    const businessUpdate = new ApplicationFlowUpdate.BusinessUpdate.Builder()
      .businessType(LegalEntitiesBusinessEntityType.SOLE_PROPRIETORSHIP)
      .build();

    const applicationFlowUpdate = new ApplicationFlowUpdate.Builder()
      .id(this.currentApplicationFlow.id)
      .business(businessUpdate)
      .build();

    try {
      await this.currentApplicationFlow.update(applicationFlowUpdate);
    } catch (error) {
      this.errorHandler.handleRpcError(error as ConnectError);
      throw error;
    }
  }

  async saveEntityTypePage(data: EntityTypeScreenData) {
    let businessUpdate =
      new ApplicationFlowUpdate.BusinessUpdate.Builder().businessType(
        data.entityType
      );
    if (data.taxId) {
      businessUpdate = businessUpdate.ein(data.taxId);
    }

    const applicationFlowUpdate = new ApplicationFlowUpdate.Builder()
      .id(this.currentApplicationFlow.id)
      .business(businessUpdate.build())
      .build();

    try {
      await this.currentApplicationFlow.update(applicationFlowUpdate);
    } catch (error) {
      this.errorHandler.handleRpcError(error as ConnectError);
      throw error;
    }
  }

  async saveBusinessManagmentPage(data: BusinessManagementScreenData) {
    const isOwner = Boolean(data.ownershipBps && data.ownershipBps > 0);
    const isFullOwner = isOwner && data.ownershipBps === MAX_OWNERSHIP_BPS;

    const individualUpdate =
      new ApplicationFlowUpdate.IndividualUpdate.Builder()
        .id(this.currentApplicationFlow.individual?.id)
        .hasSignificantOwnership(isOwner)
        .isManager(data.isManager)
        .build();
    const applicationFlowUpdate = new ApplicationFlowUpdate.Builder()
      .id(this.currentApplicationFlow.id)
      .individual(individualUpdate);

    const isSingleMemberLLC =
      isFullOwner &&
      this.state.entityType ===
        LegalEntitiesBusinessEntityType.LIMITED_LIABILITY_COMPANY;
    if (isSingleMemberLLC) {
      const businessUpdate = new ApplicationFlowUpdate.BusinessUpdate.Builder()
        .businessType(
          LegalEntitiesBusinessEntityType.SINGLE_MEMBER_LIMITED_LIABILITY_COMPANY
        )
        .build();
      applicationFlowUpdate.business(businessUpdate);
    }

    try {
      await this.currentApplicationFlow.update(applicationFlowUpdate.build());
    } catch (error) {
      this.errorHandler.handleRpcError(error as ConnectError);
      throw error;
    }
  }
}
